import type { UpdateUserRolesMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export type UpdateUserRolesTRPCInput = {
  userId: string;
  roleIds: string[];
};

type TrpcUpdateUserRolesResponse = {
  roles: { id: string; name: string; description: string }[];
};

const mapTrpcResponseToGraphQL = (
  trpcData: TrpcUpdateUserRolesResponse
): UpdateUserRolesMutation => ({
  update_user_roles: {
    roles: trpcData.roles,
  },
});

export const useUpdateUserRolesTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.user.updateRoles.mutationOptions({
      onSuccess: async () => {
        // Invalidate users list and per-user read paths so the modal and
        // settings table reflect the updated role membership.
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.user.users.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.user.userById.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.user.userByIdWithRoles.queryKey(),
          }),
        ]);
      },
    }),
  });

  return {
    updateUserRoles: async (
      variables: UpdateUserRolesTRPCInput
    ): Promise<UpdateUserRolesMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
